// SearchForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Przykładowe marki i modele
const carData = {
  Audi: ['A1','A3','A4','A5','A6','A7','A8','Q3','Q5','Q7','Q8','TT','R8'],
  BMW: [
    'Seria 1','Seria 2','Seria 3','Seria 4','Seria 5','Seria 6',
    'Seria 7','Seria 8','X1','X2','X3','X4','X5','X6','X7'
  ],
  Mercedes: [
    'Klasa A','Klasa B','Klasa C','Klasa E','Klasa S','CLA','CLS','GLA',
    'GLB','GLC','GLE','GLS'
  ],
  Volkswagen: [
    'Golf','Polo','Passat','Arteon','T-Roc','T-Cross','Tiguan','Touareg',
    'ID.3','ID.4'
  ],
  Toyota: [
    'Yaris','Corolla','Camry','RAV4','C-HR','Highlander','Land Cruiser','Supra','Prius'
  ],
  Ford: ['Fiesta','Focus','Mondeo','Kuga','Puma','Edge','Mustang','Explorer'],
  Opel: ['Corsa','Astra','Insignia','Crossland','Grandland','Mokka'],
  Hyundai: ['i20','i30','i40','Kona','Tucson','Santa Fe','IONIQ','NEXO'],
  Kia: ['Picanto','Rio','Ceed','Proceed','Stinger','XCeed','Sportage','Sorento'],
  Škoda: ['Fabia','Scala','Octavia','Superb','Kamiq','Karoq','Kodiaq','Enyaq'],
};

// Funkcja generująca listę lat (np. 1990 -> aktualny)
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 1990; y--) {
    years.push(y);
  }
  return years;
};

export default function SearchForm() {
  const navigate = useNavigate();

  // Stan formularza
  const [formData, setFormData] = useState({
    // ----- PODSTAWOWE -----
    brand: '',
    model: '',
    yearFrom: '',
    yearTo: '',
    priceFrom: '',
    priceTo: '',
    mileageFrom: '',
    mileageTo: '',

    // ----- ZAAWANSOWANE -----
    condition: '',         // Nowy / Używany
    accidentStatus: '',    // Bezwypadkowy / Powypadkowy
    damageStatus: '',      // Nieuszkodzony / Uszkodzony
    tuning: '',            // Tak / Nie
    imported: '',          // Tak / Nie
    registeredInPL: '',    // Tak / Nie
    firstOwner: '',        // Tak / Nie
    disabledAdapted: '',   // Tak / Nie

    generation: '',
    version: '',
    bodyType: '',          // Np. Hatchback / Sedan / ...
    color: '',
    fuelType: '',
    power: '',
    engineSize: '',
    transmission: '',      // Manual / Automat / ...
    drive: '',             // Przedni / Tylny / 4x4
    doors: '',
    weight: '',
    countryOfOrigin: '',
    voivodeship: '',
    city: ''
  });

  // Modele dostępne w zależności od brand
  const [availableModels, setAvailableModels] = useState([]);

  // Czy widok mobilny
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 640);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Czy pokazać sekcję zaawansowaną (desktop)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const shouldShowAdvanced = isMobile ? true : showAdvanced;

  // Sztuczna liczba pasujących wyników
  const [matchingResults, setMatchingResults] = useState(2500);

  // Sposób liczenia wyniku (prosty, „na oko”):
  const calculateMatchingResults = (data) => {
    let base = 2500;
    if (data.brand) base *= 0.9;
    if (data.model) base *= 0.8;
    if (data.yearFrom) base *= 0.95;
    if (data.yearTo) base *= 0.95;
    if (data.priceFrom || data.priceTo) base *= 0.9;
    if (data.mileageFrom || data.mileageTo) base *= 0.9;
    if (data.condition) base *= 0.9;
    if (data.damageStatus) base *= 0.9;
    if (data.fuelType) base *= 0.8;
    // ...
    return Math.floor(base);
  };

  useEffect(() => {
    setMatchingResults(calculateMatchingResults(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'brand') {
      setAvailableModels(carData[value] || []);
      // Reset modelu
      setFormData((prev2) => ({ ...prev2, model: '' }));
    }
  };

  const handleSearch = () => {
    console.log('Parametry wyszukiwania:', formData);
    // Przekierowanie do listy ogłoszeń
    navigate('/listings');
  };

  // Podstawowa sekcja – 4 kolumny
  const BasicSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {/* Marka */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Marka
        </label>
        <select
          name="brand"
          value={formData.brand}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          {Object.keys(carData).map((make) => (
            <option key={make} value={make}>
              {make}
            </option>
          ))}
        </select>
      </div>

      {/* Model */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Model
        </label>
        <select
          name="model"
          value={formData.model}
          disabled={!formData.brand}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px] disabled:bg-gray-100"
        >
          <option value="">---</option>
          {availableModels.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {/* Rok od */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Rok od
        </label>
        <select
          name="yearFrom"
          value={formData.yearFrom}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          {generateYearOptions().map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
      </div>

      {/* Rok do */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Rok do
        </label>
        <select
          name="yearTo"
          value={formData.yearTo}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          {generateYearOptions().map((yr) => (
            <option key={yr} value={yr}>{yr}</option>
          ))}
        </select>
      </div>

      {/* Cena od */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Cena od
        </label>
        <input
          type="number"
          name="priceFrom"
          min="0"
          value={formData.priceFrom}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Cena do */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Cena do
        </label>
        <input
          type="number"
          name="priceTo"
          min="0"
          value={formData.priceTo}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Przebieg od */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Przebieg od
        </label>
        <input
          type="number"
          name="mileageFrom"
          min="0"
          value={formData.mileageFrom}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Przebieg do */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Przebieg do
        </label>
        <input
          type="number"
          name="mileageTo"
          min="0"
          value={formData.mileageTo}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>
    </div>
  );

  // Zaawansowana sekcja – 4 kolumny, sporo pól
  const AdvancedSection = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
      {/* Condition */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Stan pojazdu
        </label>
        <select
          name="condition"
          value={formData.condition}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Nowy">Nowy</option>
          <option value="Używany">Używany</option>
        </select>
      </div>

      {/* Wypadkowość */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Wypadkowość
        </label>
        <select
          name="accidentStatus"
          value={formData.accidentStatus}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Bezwypadkowy">Bezwypadkowy</option>
          <option value="Powypadkowy">Powypadkowy</option>
        </select>
      </div>

      {/* Uszkodzenia */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Uszkodzenia
        </label>
        <select
          name="damageStatus"
          value={formData.damageStatus}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Nieuszkodzony">Nieuszkodzony</option>
          <option value="Uszkodzony">Uszkodzony</option>
        </select>
      </div>

      {/* Tuning */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Tuning
        </label>
        <select
          name="tuning"
          value={formData.tuning}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Tak">Tak</option>
          <option value="Nie">Nie</option>
        </select>
      </div>

      {/* Importowany */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Importowany
        </label>
        <select
          name="imported"
          value={formData.imported}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Tak">Tak</option>
          <option value="Nie">Nie</option>
        </select>
      </div>

      {/* Zarejestrowany w PL */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Zarejestrowany w PL
        </label>
        <select
          name="registeredInPL"
          value={formData.registeredInPL}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Tak">Tak</option>
          <option value="Nie">Nie</option>
        </select>
      </div>

      {/* Pierwszy właściciel */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Pierwszy właściciel
        </label>
        <select
          name="firstOwner"
          value={formData.firstOwner}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Tak">Tak</option>
          <option value="Nie">Nie</option>
        </select>
      </div>

      {/* Dla niepełnosprawnych */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Dla niepełnosprawnych
        </label>
        <select
          name="disabledAdapted"
          value={formData.disabledAdapted}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Tak">Tak</option>
          <option value="Nie">Nie</option>
        </select>
      </div>

      {/* Generacja */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Generacja
        </label>
        <input
          type="text"
          name="generation"
          value={formData.generation}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Wersja */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Wersja
        </label>
        <input
          type="text"
          name="version"
          value={formData.version}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Nadwozie */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Typ nadwozia
        </label>
        <select
          name="bodyType"
          value={formData.bodyType}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          {[
            'Hatchback','Sedan','Kombi','SUV','Coupe','Cabrio','Minivan','Terenowe','Dostawcze'
          ].map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>

      {/* Kolor */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Kolor
        </label>
        <input
          type="text"
          name="color"
          value={formData.color}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Paliwo */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Paliwo
        </label>
        <select
          name="fuelType"
          value={formData.fuelType}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Benzyna">Benzyna</option>
          <option value="Diesel">Diesel</option>
          <option value="Benzyna+LPG">Benzyna + LPG</option>
          <option value="Elektryczny">Elektryczny</option>
          <option value="Hybryda">Hybryda</option>
        </select>
      </div>

      {/* Moc */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Moc (KM)
        </label>
        <input
          type="number"
          name="power"
          min="0"
          value={formData.power}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Pojemność silnika */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Pojemność (cm³)
        </label>
        <input
          type="number"
          name="engineSize"
          min="0"
          value={formData.engineSize}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Skrzynia biegów */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Skrzynia
        </label>
        <select
          name="transmission"
          value={formData.transmission}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Manualna">Manualna</option>
          <option value="Automatyczna">Automatyczna</option>
          <option value="Półautomatyczna">Półautomatyczna</option>
        </select>
      </div>

      {/* Napęd */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Napęd
        </label>
        <select
          name="drive"
          value={formData.drive}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        >
          <option value="">---</option>
          <option value="Przedni">Przedni</option>
          <option value="Tylny">Tylny</option>
          <option value="4x4">4x4</option>
        </select>
      </div>

      {/* Drzwi */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Liczba drzwi
        </label>
        <input
          type="number"
          name="doors"
          min="2"
          max="6"
          value={formData.doors}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Waga */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Waga (kg)
        </label>
        <input
          type="number"
          name="weight"
          min="0"
          value={formData.weight}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Kraj pochodzenia */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Kraj pochodzenia
        </label>
        <input
          type="text"
          name="countryOfOrigin"
          value={formData.countryOfOrigin}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Województwo */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Województwo
        </label>
        <input
          type="text"
          name="voivodeship"
          value={formData.voivodeship}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>

      {/* Miejscowość */}
      <div>
        <label className="block text-sm font-semibold text-[#35530A] mb-1">
          Miejscowość
        </label>
        <input
          type="text"
          name="city"
          value={formData.city}
          onChange={handleInputChange}
          className="w-full h-10 text-sm px-3 border border-[#35530A] rounded-[2px]"
        />
      </div>
    </div>
  );

  return (
    <section className="py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="border border-[#35530A] rounded-[2px] p-8 shadow-md">
          {/* Sekcja podstawowa */}
          <BasicSection />

          {/* Sekcja zaawansowana */}
          {shouldShowAdvanced && <AdvancedSection />}

          <div className="relative mt-6 flex items-center w-full">
            {/* Przycisk Zaawansowane – na desktop */}
            {!isMobile && (
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="
                  border border-[#35530A]
                  rounded-[2px]
                  px-4
                  py-2
                  text-sm
                  text-[#35530A]
                  font-semibold
                  uppercase
                  bg-white
                  hover:bg-[#2D4A06]
                  hover:text-white
                  transition-colors
                  absolute
                  left-1/2
                  -translate-x-1/2
                "
              >
                {showAdvanced
                  ? 'Ukryj zaawansowane'
                  : 'Zaawansowane wyszukiwanie'}
              </button>
            )}

            {/* Przycisk Pokaż ogłoszenia */}
            <button
              type="button"
              onClick={handleSearch}
              className="
                bg-[#35530A]
                text-white
                rounded-[2px]
                px-6
                py-2
                text-sm
                font-bold
                uppercase
                hover:bg-[#2D4A06]
                transition-colors
                flex
                items-center
                gap-2
                ml-auto
              "
            >
              <span>Pokaż ogłoszenia</span>
              <span className="text-xs bg-[#35530A] text-white px-2 py-0.5 rounded">
                ({matchingResults})
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
